import React from 'react';
import { BOOK_NAMES } from '../utils/bibleBooks';
import { getBookIntroCache, getChapterCache } from './cache';
import { chaptersForBook, parseStoredVerses } from './storedText';
import { buildOutlineTree, inferEndRefs } from './outline';
import { OutlineTree } from './OutlineTree';

// Enough verses to fill a tall screen; the rest of the chapter is never seen
// while the page slides in, and drawing it would only slow the turn down.
const MAX_VERSES = 40;

const VERSE_ROW = { display: 'flex', gap: '10px', marginBottom: '8px', lineHeight: 1.75 };
const VERSE_NUM = { fontSize: '11px', color: 'var(--text-muted)', minWidth: '22px', paddingTop: '4px', fontWeight: 600, flexShrink: 0 };

export const bookName = (abbr, lang) => BOOK_NAMES[abbr]?.[lang] || BOOK_NAMES[abbr]?.en || abbr;

// Grey bars standing in for text not loaded yet.
export function SkeletonLines({ count = 8 }) {
  return Array.from({ length: count }, (_, i) => (
    <div key={i} className="pt-skel-line" style={{ width: `${88 - ((i * 37) % 30)}%` }} />
  ));
}

// The chapter beside the one being read, as it slides in during a page turn
// (see src/pageturn/). Drawn from the sessionStorage chapter cache, which The
// Bible fills ahead of time for the chapters either side, in the same layout
// as the real page so the swap underneath is not noticed. Not interactive and
// carries no data-verse attributes, so neither the annotation layer nor the
// follow-audio hook ever mistakes it for the real text.
//   langs        one language, or two in parallel mode
//   audioHeight  height of the real page's audio row, mirrored as a blank
//   label        the "Genesis 2" badge in the corner
export function ChapterPreview({ book, ch, langs, fontSize, audioHeight = 0, audioClass, label }) {
  const cached = ch ? getChapterCache(book, ch) : null;
  const texts = langs.map(l => cached?.[`text_${l}`]);
  const ready = texts.every(t => t !== undefined && t !== null);
  const columns = ready ? texts.map(t => parseStoredVerses(t).slice(0, MAX_VERSES)) : null;
  const parallel = langs.length > 1;
  const byVerseB = parallel && columns ? new Map(columns[1].map(v => [v.verse, v.text])) : null;
  const style = { fontSize: fontSize + 'px' };

  return (
    <div className="pt-preview">
      <div className="pt-badge">{label}</div>
      {audioHeight > 0 && (
        <div className={audioClass}><div className="pt-ghost-bar" style={{ height: audioHeight }} /></div>
      )}
      {!columns ? (
        <div className="bible-verse-box" style={style}><SkeletonLines count={10} /></div>
      ) : parallel ? (
        <div className={`bible-verse-box bible-text-${langs[0]}`}>
          <div className="parallel-titles-row" style={{ fontWeight: 700, opacity: 0.6, marginBottom: '10px', fontSize: '13px' }}>
            <div>&nbsp;</div><div>&nbsp;</div>
          </div>
          {columns[0].map(({ verse, text }) => (
            <div key={verse} className="parallel-row" style={{ marginBottom: '8px' }}>
              <div className={`parallel-col bible-text-${langs[0]}`} style={{ ...VERSE_ROW, marginBottom: 0, ...style }}>
                <span style={VERSE_NUM}>{verse}</span><span>{text}</span>
              </div>
              <div className={`parallel-col bible-text-${langs[1]}`} style={{ ...VERSE_ROW, marginBottom: 0, ...style }}>
                <span style={VERSE_NUM}>{verse}</span><span>{byVerseB.get(verse) || ''}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`bible-verse-box bible-text-${langs[0]}`}>
          {columns[0].map(({ verse, text }) => (
            <div key={verse} style={{ ...VERSE_ROW, ...style }}>
              <span style={VERSE_NUM}>{verse}</span><span>{text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const noop = () => {};

// A book's outline page as it turns in (Genesis 50 → Exodus outline, or back
// from Exodus 1), laid out like the real one in Bible.js: the book's name over
// its chapter numbers, then the introduction, subject and outline tree. Drawn
// from the book-intro cache, which The Bible fills ahead of time for the book
// coming up; until then, the name over grey lines. Inert: it sits under the
// page-turn cover, which takes no pointer events and is hidden from assistive
// tech, and its buttons are out of the tab order.
//   introLang   the language the intro is stored in (Spanish readers get
//               English, as on the real page)
//   t           The Bible's strings, for the field labels
export function OutlinePreview({ book, lang, introLang, t, label }) {
  const name = bookName(book, lang);
  const cached = getBookIntroCache(book, introLang);
  const intro = cached?.intro;
  const tree = cached?.outline?.length ? buildOutlineTree(inferEndRefs(cached.outline)) : null;
  const fields = intro ? [
    [t.introFields.author,            intro.author],
    [t.introFields.timeOfWriting,     intro.time_of_writing],
    [t.introFields.placeOfWriting,    intro.place_of_writing],
    [t.introFields.timePeriodCovered, intro.time_period_covered],
    [t.introFields.recipient,         intro.recipient],
  ].filter(([, v]) => v) : [];

  return (
    <div className="pt-preview">
      <div className="pt-badge">{label}</div>
      <div className="bible-intro-page">
        <div className="bible-chapter-section">
          <div className="bible-chapter-section-header">
            <span className="bible-step-btn">‹</span>
            <h2 className="bible-book-heading">{name}</h2>
            <span className="bible-step-btn">›</span>
            <span className="bible-section-tag">{t.chapters}</span>
          </div>
          <div className="bible-chapter-grid">
            {chaptersForBook(book).map(ch => (
              <button key={ch} type="button" tabIndex={-1} className="bible-ch-btn">{ch}</button>
            ))}
          </div>
        </div>
        <div className="bible-intro-body">
          {!cached ? <SkeletonLines count={8} /> : (
            <>
              {fields.length > 0 && (
                <div className="bible-intro-meta">
                  {fields.map(([field, value]) => (
                    <div key={field} className="bible-intro-field">
                      <span className="bible-intro-label">{field}: </span>{value}
                    </div>
                  ))}
                </div>
              )}
              {intro?.subject && (
                <div className="bible-intro-subject">
                  <div className="bible-intro-subject-label">{t.subjectOf(name)}</div>
                  <div className="bible-intro-subject-text">{intro.subject}</div>
                </div>
              )}
              {tree && (
                <div className="bible-outline-section">
                  <div className="bible-outline-header">
                    <h3 className="bible-outline-title">{t.outline}</h3>
                    <span className="bible-expand-all-btn">{t.expandAll}</span>
                  </div>
                  <OutlineTree nodes={tree} expandAllTrigger={0} collapseAllTrigger={0} onNavigate={noop} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
